import type OrdersBatch from '~/types/OrdersBatch';

import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';
import { type FetchOrdersBatchPayload } from '~/actions/orders-batch.actions';
import {
  type BatchEntry,
  type BatchOrder,
  type BatchShipment,
  type BatchTransaction,
} from '~/types/OrdersBatch';
import { BatchSteps } from '~/types/OrdersBatch';
import { listToMapById, listToMapByObjectId } from '~/utils/list';

type ByBatchEntryId = HashMap<number, string[]>;

export type OrdersBatchState = {
  readonly batchEntries: {
    byOrderObjectId: HashMap<string, number[]>;
    data: HashMap<number, BatchEntry>;
  };
  readonly currentStep: BatchSteps;
  readonly data: Maybe<Partial<OrdersBatch>>;
  readonly orders: {
    allObjectIds: readonly string[];
    data: HashMap<string, BatchOrder>;
  };
  readonly shipments: {
    byBatchEntryId: ByBatchEntryId;
    data: HashMap<number, BatchShipment>;
  };
  readonly transactions: {
    byBatchEntryId: ByBatchEntryId;
    data: HashMap<string, BatchTransaction>;
  };
  readonly wasOrdersFetchTriggered: boolean;
};

export const initialState: OrdersBatchState = {
  batchEntries: {
    byOrderObjectId: {},
    data: {},
  },
  currentStep: BatchSteps.NONE,
  data: null,
  orders: {
    allObjectIds: [],
    data: {},
  },
  shipments: {
    byBatchEntryId: {},
    data: {},
  },
  transactions: {
    byBatchEntryId: {},
    data: {},
  },
  wasOrdersFetchTriggered: false,
};

const reducer = (state: OrdersBatchState = initialState, action: ActionTypes): OrdersBatchState => {
  switch (action.type) {
    case ActionKeys.ORDERS_BATCH_CREATE_AND_BUY:
      return {
        ...state,
      };
    case ActionKeys.BATCH_CREATE_SUCCESS:
    case ActionKeys.ORDERS_BATCH_FETCH_SUCCESS:
      const { batch } = action.payload as FetchOrdersBatchPayload;

      const batchEntryIdsByOrderObjectId = batch.batchEntries.reduce<Record<string, number[]>>(
        (acc, batchEntry) => {
          acc[batchEntry.orderObjectId] = acc[batchEntry.orderObjectId]
            ? [...acc[batchEntry.orderObjectId], batchEntry.id]
            : [batchEntry.id];
          return acc;
        },
        {},
      );

      const shipmentObjectIdsByBatchEntryId = batch.shipments.reduce<Record<string, string[]>>(
        (acc, shipment) => {
          acc[shipment.batchEntryId] = acc[shipment.batchEntryId]
            ? [...acc[shipment.batchEntryId], shipment.objectId]
            : [shipment.objectId];
          return acc;
        },
        {},
      );

      const transactionObjectIdsByBatchEntryId = batch.transactions.reduce<
        Record<string, string[]>
      >((acc, transaction) => {
        acc[transaction.batchEntryId] = acc[transaction.batchEntryId]
          ? [...acc[transaction.batchEntryId], transaction.objectId]
          : [transaction.objectId];
        return acc;
      }, {});

      return {
        ...state,
        batchEntries: {
          byOrderObjectId: batchEntryIdsByOrderObjectId,
          data: listToMapById(batch.batchEntries),
        },
        data: {
          id: batch.id,
          labelLimitRenewalDate: state.data?.labelLimitRenewalDate,
          objectCreated: batch.objectCreated,
          objectUpdated: batch.objectUpdated,
          orderGroupStatus: batch.orderGroupStatus,
          remainingLabels: state.data?.remainingLabels,
          returnShipmentGroupStatus: batch.shipmentGroupStatus,
          returnTransactionGroupStatus: batch.returnTransactionGroupStatus,
          shipmentGroupStatus: batch.shipmentGroupStatus,
          transactionGroupStatus: batch.transactionGroupStatus,
        },
        orders: {
          allObjectIds: batch.orders.map((batchOrder) => batchOrder.objectId),
          data: listToMapByObjectId(batch.orders),
        },
        shipments: {
          byBatchEntryId: shipmentObjectIdsByBatchEntryId,
          data: listToMapByObjectId(batch.shipments),
        },
        transactions: {
          byBatchEntryId: transactionObjectIdsByBatchEntryId,
          data: listToMapByObjectId(batch.transactions),
        },
      };
    case ActionKeys.BATCH_CREATE_ERROR:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_FETCH_ERROR:
      // TODO: Set some error state for said batch
      return state;
    case ActionKeys.ORDERS_BATCH_STEP_CHANGE:
      return {
        ...state,
        currentStep: action.payload.step,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_SUCCESS:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_PACKING_SLIPS_ERROR:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_SUCCESS:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_GENERATE_ALL_LABELS_ERROR:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_POLL_START:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_POLL_TIMEOUT:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_ERROR:
      return {
        ...state,
      };
    case ActionKeys.ORDERS_BATCH_ORDERS_FETCH_TRIGGERED:
      return {
        ...state,
        wasOrdersFetchTriggered: true,
      };
    case ActionKeys.ORDERS_BATCH_TRANSACTION_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          labelLimitRenewalDate: action.payload.labelRenewalDate,
          remainingLabels: action.payload.remainingLabels,
        },
      };
    default:
      return state;
  }
};

export default reducer;
